import React, { useState, useEffect } from 'react';

interface ClientAnalyticsData {
  totalClients: number;
  activeClients: number;
  newClientsInPeriod: number;
  totalTranscriptsInPeriod: number;
  averageSentimentInPeriod: number;
  topClients: Array<{
    id: string;
    name: string;
    transcriptCount: number;
    lastInteraction: string;
  }>;
  sentimentDistribution: {
    positive: number;
    neutral: number;
    negative: number;
  };
  recentActivity: Array<{
    type: 'client_added' | 'transcript_created' | 'interaction';
    clientName: string;
    timestamp: string;
    details: string;
  }>;
  timeRange: string;
  periodLabel: string;
}

const ClientAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<ClientAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/clients/analytics?timeRange=${timeRange}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          analytics?: ClientAnalyticsData;
          error?: string;
        };
        if (data.success && data.analytics) {
          setAnalytics(data.analytics);
        }
      }
    } catch (error) {
      console.error('Failed to fetch client analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const getSentimentColor = (sentiment: number) => {
    if (sentiment >= 0.7) return 'text-green-600 bg-green-50';
    if (sentiment >= 0.4) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 rounded-lg">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 rounded-lg">
      {/* Header with Time Range Selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Client Analytics</h2>
          <p className="text-gray-600 text-sm">Overview of your client relationships and interactions</p>
        </div>
        <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Cards - 5 cards in one row */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.totalClients)}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              👥
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-green-600">↗️ {analytics.activeClients}</span>
            <span className="text-gray-500 ml-1">active</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">New {analytics.periodLabel}</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.newClientsInPeriod}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              ✨
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-gray-500">Fresh connections</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversations</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.totalTranscriptsInPeriod)}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              💬
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-gray-500">{analytics.periodLabel}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Sentiment</p>
              <p className="text-2xl font-bold text-gray-900">{Math.round(analytics.averageSentimentInPeriod * 100)}%</p>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSentimentColor(analytics.averageSentimentInPeriod)}`}>
              {analytics.averageSentimentInPeriod >= 0.7 ? '😊' : analytics.averageSentimentInPeriod >= 0.4 ? '😐' : '😟'}
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-gray-500">{analytics.periodLabel}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Satisfaction</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.sentimentDistribution.positive}%</p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              📈
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-gray-500">Positive sentiment</span>
          </div>
        </div>
      </div>

      {/* Bottom Section: Top Clients, Recent Activity, and Growth Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Clients */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            🏆 Most Active Clients
          </h3>
          <div className="space-y-3">
            {analytics.topClients.slice(0, 3).map((client, index) => (
              <div key={client.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{client.name}</p>
                    <p className="text-xs text-gray-500">{formatTimeAgo(client.lastInteraction)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{client.transcriptCount}</p>
                  <p className="text-xs text-gray-500">conversations</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            📈 Recent Activity
          </h3>
          <div className="space-y-3">
            {analytics.recentActivity.slice(0, 3).map((activity, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                  activity.type === 'client_added' ? 'bg-green-100 text-green-700' :
                  activity.type === 'transcript_created' ? 'bg-blue-100 text-blue-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {activity.type === 'client_added' ? '👤' :
                   activity.type === 'transcript_created' ? '📝' : '💬'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.clientName}</span>
                  </p>
                  <p className="text-xs text-gray-500 truncate">{activity.details}</p>
                  <p className="text-xs text-gray-400">{formatTimeAgo(activity.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sentiment Breakdown */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            🎯 Sentiment Breakdown
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Positive</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{analytics.sentimentDistribution.positive}%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Neutral</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{analytics.sentimentDistribution.neutral}%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Negative</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{analytics.sentimentDistribution.negative}%</span>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                Based on {analytics.periodLabel.toLowerCase()} data
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAnalytics;
